import { useMemo } from "react";
import { safeFormat } from '@/lib/safeDate';
import { useTranslation } from "react-i18next";

import { format } from "date-fns";
import { ptBR, enUS, es } from "date-fns/locale";
import { ArrowUp, ArrowDown, ArrowUpDown, ExternalLink } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SalesReportDeal, PaymentStatus, ContractStatus, CardStatus, DealStatus, AutoChargeStatus } from "@/hooks/useSalesReport";
import { ColumnDefinition, SortConfig } from "@/hooks/useSalesReportColumns";

interface SalesReportTableProps {
  deals: SalesReportDeal[];
  columns: ColumnDefinition[];
  sortConfig: SortConfig | null;
  onSort: (columnId: string) => void;
  isLoading: boolean;
  error: Error | null;
}

export function SalesReportTable({
  deals,
  columns,
  sortConfig,
  onSort,
  isLoading,
  error,
}: SalesReportTableProps) {
  const { t, i18n } = useTranslation();

  const getLocale = () => {
    switch (i18n.language) {
      case "pt-BR": return ptBR;
      case "es": return es;
      default: return enUS;
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(i18n.language === "pt-BR" ? "pt-BR" : "en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return safeFormat(new Date(dateStr), "dd/MM/yyyy", { locale: getLocale() });
  };

  const sortedDeals = useMemo(() => {
    if (!sortConfig) return deals;

    return [...deals].sort((a, b) => {
      const aVal = (a as any)[sortConfig.column];
      const bVal = (b as any)[sortConfig.column];

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Handle dates
      if (sortConfig.column === "closed_date") {
        const aDate = new Date(aVal).getTime();
        const bDate = new Date(bVal).getTime();
        return sortConfig.direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      // Handle numbers
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      // Handle strings
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (sortConfig.direction === "asc") {
        return aStr.localeCompare(bStr);
      }
      return bStr.localeCompare(aStr);
    });
  }, [deals, sortConfig]);

  const paymentStatusConfig: Record<PaymentStatus, { labelKey: string; className: string }> = {
    settled: { labelKey: "salesReport.paymentStatus.settled", className: "bg-emerald-600 text-white dark:bg-emerald-700 dark:text-white" },
    paid: { labelKey: "salesReport.paymentStatus.paid", className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
    partial: { labelKey: "salesReport.paymentStatus.partial", className: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" },
    late: { labelKey: "salesReport.paymentStatus.late", className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
    cancelled: { labelKey: "salesReport.paymentStatus.cancelled", className: "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300" },
    no_payment: { labelKey: "salesReport.paymentStatus.noPayment", className: "bg-muted text-muted-foreground" },
  };

  const contractStatusConfig: Record<ContractStatus, { labelKey: string; className: string }> = {
    draft: { labelKey: "salesReport.contractStatus.draft", className: "bg-muted text-muted-foreground" },
    sent: { labelKey: "salesReport.contractStatus.sent", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
    completed: { labelKey: "salesReport.contractStatus.completed", className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
    no_contract: { labelKey: "salesReport.contractStatus.noContract", className: "bg-muted text-muted-foreground" },
    issues: { labelKey: "salesReport.contractStatus.issues", className: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" },
  };

  const cardStatusConfig: Record<CardStatus, { labelKey: string; className: string }> = {
    saved: { labelKey: "salesReport.cardStatus.saved", className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
    not_saved: { labelKey: "salesReport.cardStatus.notSaved", className: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" },
  };

  const dealStatusConfig: Record<DealStatus, { labelKey: string; className: string }> = {
    active: { labelKey: "salesReport.dealStatus.active", className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
    cancelled: { labelKey: "salesReport.dealStatus.cancelled", className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  };

  const autoChargeStatusConfig: Record<AutoChargeStatus, { labelKey: string; className: string }> = {
    enabled: { labelKey: "salesReport.autoChargeStatus.enabled", className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" },
    card_only: { labelKey: "salesReport.autoChargeStatus.cardOnly", className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
    no_card: { labelKey: "salesReport.autoChargeStatus.noCard", className: "bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400" },
  };

  const renderCellValue = (deal: SalesReportDeal, column: ColumnDefinition) => {
    const value = (deal as any)[column.accessor];

    if (column.id === "total_amount") {
      // Mostrar valor efetivo (aplica regra de cancelamento)
      return formatCurrency(deal.effective_amount);
    }

    if (column.id === "closed_date") {
      return value ? formatDate(value) : "-";
    }

    if (column.id === "payment_status") {
      const status = paymentStatusConfig[value as PaymentStatus] || paymentStatusConfig.no_payment;
      return <Badge variant="secondary" className={status.className}>{t(status.labelKey)}</Badge>;
    }

    if (column.id === "contract_status") {
      const status = contractStatusConfig[value as ContractStatus] || contractStatusConfig.no_contract;
      return <Badge variant="secondary" className={status.className}>{t(status.labelKey)}</Badge>;
    }

    if (column.id === "card_status") {
      const status = cardStatusConfig[value as CardStatus] || cardStatusConfig.not_saved;
      return <Badge variant="secondary" className={status.className}>{t(status.labelKey)}</Badge>;
    }

    if (column.id === "deal_status") {
      const status = dealStatusConfig[value as DealStatus] || dealStatusConfig.active;
      return <Badge variant="secondary" className={status.className}>{t(status.labelKey)}</Badge>;
    }

    if (column.id === "auto_charge_status") {
      const status = autoChargeStatusConfig[value as AutoChargeStatus] || autoChargeStatusConfig.no_card;
      return <Badge variant="secondary" className={status.className}>{t(status.labelKey)}</Badge>;
    }

    if (column.id === "actions") {
      return (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => window.open(`/deals/${deal.id}`, '_blank')}
          title={t("salesReport.viewDeal")}
        >
          <ExternalLink className="h-4 w-4" />
        </Button>
      );
    }

    return value || "-";
  };

  const getSortIcon = (columnId: string) => {
    if (sortConfig?.column !== columnId) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    if (sortConfig.direction === "asc") {
      return <ArrowUp className="h-4 w-4 ml-1" />;
    }
    return <ArrowDown className="h-4 w-4 ml-1" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-muted-foreground">{t("common.loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-destructive">{t("common.error")}</p>
      </div>
    );
  }

  if (!deals.length) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-muted-foreground">{t("salesReport.noDealsFound")}</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((column) => (
            <TableHead
              key={column.id}
              className={`${column.align === "right" ? "text-right" : ""} ${
                column.sortable ? "cursor-pointer select-none hover:bg-muted/50" : ""
              }`}
              onClick={() => column.sortable && onSort(column.id)}
            >
              <div className={`flex items-center ${column.align === "right" ? "justify-end" : ""}`}>
                {t(column.labelKey)}
                {column.sortable && getSortIcon(column.id)}
              </div>
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedDeals.map((deal) => (
          <TableRow key={deal.id}>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                className={`${column.align === "right" ? "text-right" : ""} ${
                  column.id === "name" ? "font-medium" : ""
                }`}
              >
                {renderCellValue(deal, column)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
